/**
 * Shared full-screen reward ceremony — rays, scrim, icon + amount cluster, Claim.
 */
import { gameImageUrl } from "../assets/game-image-urls.js";
import { getGrantRewardDisplayItems, formatGrantToast } from "./retention-hooks.js";
import { prefersReducedMotion } from "./sky-background-prefs.js";

export var WHEEL_REWARD_PREVIEW_MS = 1000;
export var REWARD_REVEAL_ENTER_MS = 360;
export var REWARD_REVEAL_FADE_MS = 420;

/**
 * @param {number} index
 * @param {number} count
 * @returns {{ angleDeg: number, useRing: boolean }}
 */
export function getRewardRevealItemLayout(index, count) {
  var n = Math.max(1, Math.floor(Number(count) || 1));
  var i = Math.max(0, Math.floor(Number(index) || 0));
  if (n <= 1) return { angleDeg: 0, useRing: false };
  var angleDeg = (360 / n) * i - 90;
  return { angleDeg: angleDeg, useRing: true };
}

/**
 * @param {Record<string, HTMLElement | null | undefined>} dom
 */
export function createRewardReveal(dom) {
  return new RewardReveal(dom);
}

/**
 * @param {Record<string, HTMLElement | null | undefined>} dom
 */
function RewardReveal(dom) {
  this.overlay = dom.rewardRevealOverlay || null;
  this.itemsMount = dom.rewardRevealItems || null;
  this.claimBtn = dom.rewardRevealClaimBtn || null;
  this._open = false;
  this._closing = false;
  this._onClaim = null;
  this._onClosed = null;
  this._fadeTimer = 0;
  this._boundClaim = this._handleClaim.bind(this);
}

RewardReveal.prototype.init = function () {
  if (this.claimBtn && !this.claimBtn.dataset.rewardRevealBound) {
    this.claimBtn.dataset.rewardRevealBound = "1";
    this.claimBtn.addEventListener("click", this._boundClaim);
  }
};

RewardReveal.prototype.isOpen = function () {
  return !!this._open;
};

/**
 * @param {{
 *   grant?: import("../config/retention-config.js").RetentionGrant,
 *   onClaim?: () => void,
 *   onClosed?: () => void,
 * }} opts
 */
RewardReveal.prototype.open = function (opts) {
  opts = opts || {};
  if (!this.overlay || this._open || this._closing) return;
  var grant = opts.grant || {};
  var items = getGrantRewardDisplayItems(grant);
  if (!items.length) return;

  this._onClaim = typeof opts.onClaim === "function" ? opts.onClaim : null;
  this._onClosed = typeof opts.onClosed === "function" ? opts.onClosed : null;
  this._renderItems(items, grant);
  this._open = true;
  this._closing = false;

  this.overlay.classList.remove("hidden", "reward-reveal-overlay--closing");
  this.overlay.setAttribute("aria-hidden", "false");
  if (this.claimBtn) this.claimBtn.disabled = false;

  if (prefersReducedMotion()) {
    this.overlay.classList.add("reward-reveal-overlay--reduced-motion");
  } else {
    this.overlay.classList.remove("reward-reveal-overlay--reduced-motion");
  }

  if (typeof this.overlay.offsetWidth === "number") {
    void this.overlay.offsetWidth;
  }
  this.overlay.classList.add("reward-reveal-overlay--visible");
};

RewardReveal.prototype._renderItems = function (items, grant) {
  if (!this.itemsMount) return;
  this.itemsMount.innerHTML = "";
  this.itemsMount.dataset.count = String(items.length);
  this.itemsMount.setAttribute("aria-label", formatGrantToast(grant));

  for (var i = 0; i < items.length; i++) {
    var item = items[i];
    var layout = getRewardRevealItemLayout(i, items.length);
    var cell = document.createElement("div");
    cell.className = "reward-reveal-overlay__item";
    if (layout.useRing) {
      cell.style.setProperty("--reward-item-angle", layout.angleDeg + "deg");
    }

    var icon = document.createElement("img");
    icon.className = "reward-reveal-overlay__icon";
    icon.alt = "";
    icon.setAttribute("aria-hidden", "true");
    icon.setAttribute("data-game-img", item.icon);
    var url = gameImageUrl(item.icon);
    if (url) icon.src = url;

    var amount = document.createElement("span");
    amount.className = "reward-reveal-overlay__amount";
    amount.textContent = item.amount;

    cell.appendChild(icon);
    cell.appendChild(amount);
    this.itemsMount.appendChild(cell);
  }
};

RewardReveal.prototype._handleClaim = function () {
  if (!this._open || this._closing) return;
  var onClaim = this._onClaim;
  this._onClaim = null;
  if (onClaim) onClaim();
  this.close();
};

RewardReveal.prototype.close = function () {
  if (!this.overlay || !this._open || this._closing) return;
  this._closing = true;
  if (this.claimBtn) this.claimBtn.disabled = true;
  this.overlay.classList.remove("reward-reveal-overlay--visible");
  this.overlay.classList.add("reward-reveal-overlay--closing");

  var self = this;
  var overlay = this.overlay;
  var onClosed = this._onClosed;
  this._onClosed = null;
  var finished = false;

  var finish = function () {
    if (finished) return;
    finished = true;
    if (self._fadeTimer) {
      clearTimeout(self._fadeTimer);
      self._fadeTimer = 0;
    }
    if (overlay && typeof overlay.removeEventListener === "function") {
      overlay.removeEventListener("animationend", onAnimationEnd);
    }
    self._open = false;
    self._closing = false;
    if (overlay) {
      overlay.classList.add("hidden");
      overlay.classList.remove("reward-reveal-overlay--closing");
      overlay.setAttribute("aria-hidden", "true");
    }
    if (onClosed) onClosed();
  };

  var onAnimationEnd = function (e) {
    if (!e || e.target !== overlay) return;
    if (
      e.animationName !== "reward-reveal-overlay-fade-out"
      && e.animationName !== "reward-reveal-overlay-fade-out-reduced"
    ) {
      return;
    }
    finish();
  };

  if (typeof overlay.addEventListener === "function") {
    overlay.addEventListener("animationend", onAnimationEnd);
  }
  if (this._fadeTimer) clearTimeout(this._fadeTimer);
  this._fadeTimer = setTimeout(finish, REWARD_REVEAL_FADE_MS + 80);
};